import { Download, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import MobileDownloadButton from "./MobileDownloadButton";
import api from "../lib/api";

// Auto-hide threshold — when QR conversion drops below this, the landing
// page hides the QR card and surfaces only the smart MobileDownloadButton
// instead. The threshold is intentionally generous (10%) because a low
// rate means the QR isn't pulling its weight and we shouldn't show it
// to fresh visitors. Confidence floor: we keep showing the card until
// we have at least N attributions so a brand-new install doesn't
// trigger a false-negative hide.
const QR_HIDE_THRESHOLD = 10;       // %
const QR_HIDE_CONFIDENCE_FLOOR = 25; // attributions

/**
 * LandingPhoneMockup — a high-fidelity phone-shaped SVG hero visual that
 * shows Cosmic Worm Breaker rendered "in-screen". Replaces the older flat
 * sci-fi panel on the public landing page so first-time visitors instantly
 * see what the game looks like AND understand that it's installable on
 * their phone.
 *
 * Design choices:
 *  - Pure SVG/CSS, no extra dependencies. Crisp at any density.
 *  - Slight Y-axis rotation + drop-shadow → a confident product-shot feel
 *    (think Apple keynote stills) without burning visual budget.
 *  - Animated scanline + idle ball drift via CSS keyframes (already
 *    available in the project's index.css `scanlines` class — we
 *    extend with a local `landing-mockup` scope).
 *  - Includes a non-clickable "INSTALL ON MOBILE" hint badge underneath
 *    so the surface communicates downloadability without competing with
 *    the navbar's real `<PwaInstallButton>`.
 *
 * All interactive CTAs stay in the hero left column — this component is
 * purely visual.
 */
export default function LandingPhoneMockup() {
  // Encode the public landing URL into a QR code so desktop visitors can
  // hop straight to the installable PWA on their phone without retyping
  // the address. We resolve the URL on mount (rather than from
  // `process.env.REACT_APP_BACKEND_URL`) so localhost/preview/prod each
  // produce a scannable QR pointing at THEIR own origin — never a stale
  // build-time constant.
  const [qrUrl, setQrUrl] = useState(null);
  // Self-healing: if QR conversion drops below QR_HIDE_THRESHOLD with
  // enough confidence, hide the QR card automatically and rely on the
  // smart MobileDownloadButton instead. Default to showing the card
  // until the summary endpoint returns — never block hero paint on the
  // telemetry request.
  const [qrCardEnabled, setQrCardEnabled] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL("/", window.location.origin);
    url.searchParams.set("src", "qr");
    setQrUrl(url.toString());
    // Lifetime window — using lifetime here (not the active admin
    // window) so brand-new pilots aren't shown a card just because
    // today's data is sparse. Failure leaves `qrCardEnabled = true`
    // so a missing endpoint never breaks the visual.
    api
      .get("/telemetry/install-attribution/summary?window=lifetime")
      .then((r) => {
        const att = Number(r.data?.attributed?.qr || 0);
        const inst = Number(r.data?.completed?.qr || 0);
        if (att >= QR_HIDE_CONFIDENCE_FLOOR) {
          const conv = Math.round((inst / att) * 100);
          setQrCardEnabled(conv >= QR_HIDE_THRESHOLD);
        }
      })
      .catch(() => {});
  }, []);
  return (
    <div
      className="relative w-full max-w-[420px] mx-auto"
      data-testid="landing-phone-mockup"
    >
      {/* Ambient glow — sits behind the phone, fades at the edges. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 blur-3xl"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 45%, rgba(176,38,255,0.45) 0%, rgba(0,255,255,0.18) 45%, rgba(0,0,0,0) 75%)",
        }}
      />
      {/* Floating purple worm decoration — peeking out from behind the
          phone bottom-left, like the mascot is escaping the screen. */}
      <svg
        aria-hidden
        viewBox="0 0 200 80"
        className="absolute -left-6 -bottom-3 w-32 sm:w-40 opacity-90"
        style={{ filter: "drop-shadow(0 0 14px rgba(176,38,255,0.55))" }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <circle
            key={i}
            cx={20 + i * 28}
            cy={40 + Math.sin(i * 0.7) * 14}
            r={14 - i * 1.5}
            fill="#b026ff"
            opacity={1 - i * 0.13}
          />
        ))}
        {/* Worm eye */}
        <circle cx={28} cy={36} r={2.6} fill="#ffffff" />
        <circle cx={29} cy={36} r={1.2} fill="#08080e" />
      </svg>

      {/* Phone */}
      <div
        className="relative mx-auto landing-mockup-tilt"
        style={{
          width: "100%",
          aspectRatio: "9 / 18",
          maxWidth: 320,
        }}
      >
        {/* Outer chassis — brushed dark with a subtle inner highlight. */}
        <div
          className="absolute inset-0 rounded-[40px]"
          style={{
            background:
              "linear-gradient(160deg, #1a1a24 0%, #08080e 55%, #14141c 100%)",
            border: "1.5px solid rgba(255,255,255,0.08)",
            boxShadow:
              "0 30px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,255,255,0.15), 0 0 40px rgba(176,38,255,0.25)",
          }}
        />
        {/* Side button accents */}
        <div className="absolute -left-[2px] top-[18%] w-[3px] h-10 rounded-l bg-[#1f1f29]" />
        <div className="absolute -left-[2px] top-[28%] w-[3px] h-16 rounded-l bg-[#1f1f29]" />
        <div className="absolute -right-[2px] top-[24%] w-[3px] h-20 rounded-r bg-[#1f1f29]" />

        {/* Screen bezel */}
        <div
          className="absolute inset-[8px] rounded-[34px] overflow-hidden"
          style={{
            background: "#02020a",
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 0 20px rgba(0,255,255,0.06)",
          }}
        >
          {/* Dynamic-island style notch */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-2 w-[34%] h-5 rounded-full z-20 flex items-center justify-end pr-2"
            style={{ background: "#000" }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: "#00ffff",
                boxShadow: "0 0 6px #00ffff",
              }}
            />
          </div>

          {/* Game scene */}
          <div className="absolute inset-0 px-4 pt-10 pb-6 flex flex-col">
            {/* HUD strip */}
            <div className="flex items-center justify-between font-mono-cosmic text-[7px] tracking-[0.2em] uppercase text-white/65 mb-2">
              <span className="text-[#00ffff]">// LVL 07</span>
              <span className="text-[#ffd700]">12,480 PTS</span>
              <span className="text-[#ff007f]">♥♥♥</span>
            </div>

            {/* Starfield gradient */}
            <div
              className="relative flex-1 overflow-hidden rounded-[12px]"
              style={{
                background:
                  "linear-gradient(180deg, #0a0a1e 0%, #1a0a2e 100%)",
                boxShadow: "inset 0 0 24px rgba(176,38,255,0.18)",
              }}
            >
              {/* Stars */}
              <svg
                aria-hidden
                viewBox="0 0 100 200"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full opacity-70"
              >
                {Array.from({ length: 28 }).map((_, i) => {
                  const x = (i * 37) % 100;
                  const y = (i * 53) % 200;
                  const r = ((i % 3) + 1) * 0.3;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r={r}
                      fill="#ffffff"
                      opacity={0.45 + (i % 4) * 0.12}
                    />
                  );
                })}
              </svg>

              {/* Scanlines */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
                style={{
                  background:
                    "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                }}
              />

              {/* Brick grid */}
              <div className="absolute inset-x-3 top-3 grid grid-cols-5 gap-1.5">
                {[
                  // 3 rows × 5 cols of bricks. Color per row.
                  ...Array(5).fill("#00ffff"),
                  ...Array(5).fill("#ffd700"),
                  ...Array(5).fill("#ff007f"),
                ].map((c, i) => (
                  <div
                    key={i}
                    className="h-3 rounded-[3px] landing-mockup-brick"
                    style={{
                      background: c,
                      boxShadow: `0 0 6px ${c}cc, inset 0 1px 0 rgba(255,255,255,0.5)`,
                      animationDelay: `${(i % 5) * 0.12}s`,
                      // Two bricks "destroyed" for variety
                      opacity: i === 7 || i === 12 ? 0 : 1,
                    }}
                  />
                ))}
              </div>

              {/* Ball */}
              <div
                className="absolute landing-mockup-ball"
                style={{
                  width: 10,
                  height: 10,
                  left: "42%",
                  top: "55%",
                  borderRadius: 9999,
                  background: "#ffffff",
                  boxShadow:
                    "0 0 8px #00ffff, 0 0 16px rgba(0,255,255,0.55)",
                }}
              />

              {/* Paddle (purple worm) */}
              <div
                className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center"
                style={{ filter: "drop-shadow(0 0 8px rgba(176,38,255,0.7))" }}
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 16 - i * 1.2,
                      height: 16 - i * 1.2,
                      borderRadius: 9999,
                      background: "#b026ff",
                      marginLeft: i === 0 ? 0 : -3,
                      opacity: 1 - i * 0.08,
                    }}
                  />
                ))}
              </div>

              {/* Power-up token drifting down */}
              <div
                className="absolute landing-mockup-powerup"
                style={{
                  left: "62%",
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, #ffd700, #ff007f)",
                  boxShadow: "0 0 10px rgba(255,215,0,0.8)",
                }}
              />
            </div>

            {/* Bottom controls hint */}
            <div className="mt-3 flex items-center justify-between font-mono-cosmic text-[7px] tracking-[0.25em] uppercase">
              <span className="text-white/40">◀ DRAG ▶</span>
              <span className="text-[#00ffff]">TAP LAUNCH</span>
            </div>
          </div>

          {/* Edge vignette */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow:
                "inset 0 0 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
              borderRadius: 34,
            }}
          />
        </div>
      </div>

      {/* Live download CTA — replaces the older static "Installable PWA"
          hint badge. Honors the device's install path (native prompt on
          Chrome/Android, share-sheet modal on iOS, QR fallback on
          desktop) and flips to a "App Installed" badge once installed. */}
      <div className="mt-6 flex flex-col items-center gap-2">
        <MobileDownloadButton testId="phone-mockup-download-btn" />
        <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40 flex items-center gap-2">
          <Smartphone className="w-3 h-3 text-[#00ffff]" />
          Works offline
          <span className="text-white/20">·</span>
          <Download className="w-3 h-3 text-[#ffd700]" />
          No app store
        </div>
      </div>

      {/* QR code card — desktop-only. Mobile visitors are already on their
          phone, so the install prompt is one tap away in the navbar; the
          QR is for desktop visitors who would otherwise lose the link
          before getting to their phone. */}
      {qrUrl && qrCardEnabled && (
        <div
          className="hidden md:flex mt-5 mx-auto items-center gap-4 px-4 py-3 border border-white/12 bg-white/[0.03] backdrop-blur-sm max-w-[300px]"
          data-testid="landing-phone-mockup-qr"
        >
          <div
            className="flex-none p-1.5 bg-white rounded-sm"
            style={{ lineHeight: 0 }}
            title="Scan to install Cosmic Worm Breaker on your phone"
          >
            <QRCodeSVG
              value={qrUrl}
              size={68}
              bgColor="#ffffff"
              fgColor="#08080e"
              level="M"
              marginSize={0}
            />
          </div>
          <div className="min-w-0">
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#00ffff] mb-0.5">
              // SCAN TO INSTALL
            </div>
            <div className="font-mono-cosmic text-[10px] leading-snug text-white/70">
              Point your phone camera here — open the link, then{" "}
              <span className="text-[#ffd700]">Add to Home Screen</span>.
            </div>
          </div>
        </div>
      )}

      <style>{`
        .landing-mockup-tilt {
          transform: rotate(-3deg);
          animation: landing-mockup-float 6s ease-in-out infinite;
        }
        @keyframes landing-mockup-float {
          0%, 100% { transform: rotate(-3deg) translateY(0); }
          50%      { transform: rotate(-3deg) translateY(-6px); }
        }
        .landing-mockup-ball {
          animation: landing-mockup-ball-drift 3.2s ease-in-out infinite;
        }
        @keyframes landing-mockup-ball-drift {
          0%, 100% { transform: translate(0, 0); }
          25%      { transform: translate(18px, -8px); }
          50%      { transform: translate(34px, 4px); }
          75%      { transform: translate(12px, 14px); }
        }
        .landing-mockup-powerup {
          top: 18%;
          animation: landing-mockup-powerup-drop 4s linear infinite;
        }
        @keyframes landing-mockup-powerup-drop {
          0%   { top: 18%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 78%; opacity: 0; }
        }
        .landing-mockup-brick {
          animation: landing-mockup-brick-pulse 4s ease-in-out infinite;
        }
        @keyframes landing-mockup-brick-pulse {
          0%, 100% { filter: brightness(1); }
          50%      { filter: brightness(1.25); }
        }
        @media (prefers-reduced-motion: reduce) {
          .landing-mockup-tilt,
          .landing-mockup-ball,
          .landing-mockup-powerup,
          .landing-mockup-brick { animation: none; }
        }
      `}</style>
    </div>
  );
}
